import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Route, Loader2, MapPin, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RunCreateModal from "@/components/scheduler/RunCreateModal";

const dstr = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const RISK = {
  red: { c: "bg-rose-50 text-rose-700 border-rose-200", t: "Disruption" },
  amber: { c: "bg-amber-50 text-amber-700 border-amber-200", t: "Possible delay" },
  green: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", t: "Clear" },
  grey: { c: "bg-slate-50 text-slate-500 border-slate-200", t: "No data" },
};

const isToday = (r) => {
  const t = new Date().toISOString().slice(0, 10);
  return (r.days || []).some((d) => d.date === t);
};

export default function RunsPage() {
  const navigate = useNavigate();
  const { canAny } = useAuth();
  const canWrite = canAny(["operations.schedule", "operations.manage"]);
  const [items, setItems] = useState(null);
  const [onlyDisrupted, setOnlyDisrupted] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => api.get("/runs").then((r) => setItems(r.data.items || [])).catch((e) => { setItems([]); toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load runs"); });
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (items || []).filter((r) => !onlyDisrupted || (["amber", "red"].includes(r.disruption_risk) && isToday(r))),
    [items, onlyDisrupted],
  );

  const del = async (r) => {
    if (!window.confirm(`Delete run "${r.name}"? The shifts are kept and simply unlinked.`)) return;
    try {
      await api.delete(`/runs/${r.run_id}`);
      toast.success("Run deleted");
      setItems((prev) => (prev || []).filter((x) => x.run_id !== r.run_id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete run");
    }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-runs">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <PageIcon />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Runs</h1>
          <p className="text-sm text-slate-500 mt-0.5">Saved multi-day runs built from selected shifts</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setOnlyDisrupted((v) => !v)}
            data-testid="runs-filter-disrupted"
            className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg border px-3 py-1.5 transition-colors ${
              onlyDisrupted ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-200 hover:border-rose-200 hover:text-rose-600"}`}>
            <AlertTriangle className="w-4 h-4" /> Show only disrupted runs today
          </button>
          <button
            onClick={() => navigate("/reports/hotspots")}
            data-testid="runs-hotspots-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg border border-slate-200 bg-white text-slate-600 px-3 py-1.5 hover:border-indigo-200 hover:text-indigo-600 transition-colors">
            <MapPin className="w-4 h-4" /> Hotspot report
          </button>
          {canWrite && (
            <button
              onClick={() => setShowCreate(true)}
              data-testid="run-add-btn"
              className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg bg-indigo-600 text-white px-3.5 py-1.5 hover:bg-indigo-700 transition-colors shadow-sm">
              <Plus className="w-4 h-4" /> Add run
            </button>
          )}
        </div>
      </div>
      {items === null ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="runs-empty">
          {onlyDisrupted ? "No disrupted runs scheduled today." : (
            <>No saved runs yet.{canWrite ? " Use “Add run” above to build one." : " Build one from the Scheduler → “Create run”."}</>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="runs-grid">
          {filtered.map((r) => {
            const rk = RISK[r.disruption_risk] || RISK.grey;
            return (
              <div key={r.run_id} role="button" tabIndex={0}
                onClick={() => navigate(`/runs/${r.run_id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/runs/${r.run_id}`); }}
                className="relative cursor-pointer text-left bg-white border border-slate-200 rounded-2xl shadow-sm p-5 hover:shadow-md hover:border-indigo-200 transition-all" data-testid={`run-card-${r.run_id}`}>
                {canWrite && (
                  <button onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`run-delete-${r.run_id}`}
                    title="Delete run"
                    className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <h2 className="text-base font-bold text-slate-900 mb-1 pr-8">{r.name}</h2>
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2 py-0.5 mb-2 ${rk.c}`} data-testid={`run-risk-badge-${r.run_id}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" /> {rk.t}
                </span>
                <p className="text-sm text-slate-500 mb-3">{(r.days || []).length} days · {r.shift_count} stops</p>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {(r.days || [])[0]?.date ? dstr((r.days || [])[0].date) : "—"}</span>
                  <span className="font-mono">{r.run_id}</span>
                  <span className="ml-auto">Created {dstr(r.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <RunCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}
